<script>
import Layout from "@/router/layouts/main";
import StPageHeader from "@/components/base/st-page-header";
import { CHANGELOG, APP_VERSION, rotuloVersao, formatarDataVersao, marcarVersaoVista, versoesNaoVistas } from "@/utils/versao";

const TIPOS = {
  novo: { label: "Novo", icon: "bx-star", classe: "is-novo" },
  melhoria: { label: "Melhoria", icon: "bx-trending-up", classe: "is-melhoria" },
  correcao: { label: "Correção", icon: "bx-wrench", classe: "is-correcao" },
};

/**
 * Novidades — histórico de versões do sistema (fonte: utils/versao.js).
 * Abrir esta página marca a versão atual como vista: apaga o selo "Nova" do
 * menu lateral e a notificação do sino.
 */
export default {
  components: { Layout, StPageHeader },
  data() {
    // Guardado ANTES de marcar como vista, para destacar o que era novo
    // para este usuário nesta visita.
    const novas = versoesNaoVistas().map((v) => v.version);
    return { versoes: CHANGELOG, novas, atual: APP_VERSION };
  },
  mounted() {
    marcarVersaoVista();
  },
  methods: {
    rotulo: rotuloVersao,
    data: formatarDataVersao,
    tipo(t) {
      return TIPOS[t] || TIPOS.melhoria;
    },
    contar(versao, t) {
      return versao.items.filter((i) => i.type === t).length;
    },
  },
};
</script>

<template>
  <Layout>
    <StPageHeader title="Novidades" :breadcrumb="[{ text: 'Novidades' }]"
      subtitle="O que mudou no Sales Teams a cada versão." />

    <ol class="st-changelog">
      <li v-for="(v, i) in versoes" :key="v.version" class="st-changelog-entry"
        :class="{ 'is-current': v.version === atual }" :style="{ animationDelay: i * 60 + 'ms' }">
        <div class="st-changelog-marker" aria-hidden="true"></div>

        <article class="card st-changelog-card" :class="{ 'card-glow': i === 0 }">
          <div class="card-body">
            <header class="st-changelog-head">
              <span class="st-changelog-version">{{ rotulo(v.version) }}</span>
              <span v-if="v.version === atual" class="badge badge-success badge-dot">Versão atual</span>
              <span v-else-if="novas.includes(v.version)" class="badge badge-info">Novo para você</span>
              <time class="st-changelog-date" :datetime="v.date">{{ data(v.date) }}</time>
            </header>

            <h2 class="st-changelog-title">{{ v.title }}</h2>
            <p class="st-changelog-summary">{{ v.summary }}</p>

            <ul class="st-changelog-items">
              <li v-for="(item, j) in v.items" :key="j" class="st-changelog-item">
                <span class="st-changelog-tag" :class="tipo(item.type).classe">
                  <i class="bx" :class="tipo(item.type).icon" aria-hidden="true"></i>{{ tipo(item.type).label }}
                </span>
                <span>{{ item.text }}</span>
              </li>
            </ul>
          </div>
        </article>
      </li>
    </ol>
  </Layout>
</template>

<style scoped>
/* Linha do tempo: marcador + guia vertical à esquerda de cada versão. */
.st-changelog {
  list-style: none;
  margin: 0;
  padding: 0 0 0 1.75rem;
  position: relative;
  max-width: 860px;
}

.st-changelog::before {
  content: "";
  position: absolute;
  left: 0.45rem;
  top: 0.75rem;
  bottom: 0.75rem;
  width: 2px;
  border-radius: 2px;
  background: linear-gradient(180deg, var(--st-accent), var(--st-border-soft) 30%);
}

.st-changelog-entry {
  position: relative;
  animation: st-changelog-in var(--st-duration-slow) var(--st-ease-out) backwards;
}

.st-changelog-marker {
  position: absolute;
  left: -1.75rem;
  top: 1.35rem;
  width: 0.95rem;
  height: 0.95rem;
  margin-left: 0.03rem;
  border-radius: 50%;
  background: var(--st-surface);
  border: 2px solid var(--st-border);
}

.is-current .st-changelog-marker {
  border-color: var(--st-accent);
  background: var(--st-accent);
  box-shadow: 0 0 0 4px var(--st-accent-soft);
}

.st-changelog-card {
  margin-bottom: 1.25rem;
}

.st-changelog-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.st-changelog-version {
  font-weight: 800;
  font-size: 0.85rem;
  letter-spacing: 0.02em;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  color: var(--st-text-inverse);
  background: var(--st-action-gradient);
}

.st-changelog-date {
  margin-left: auto;
  font-size: 0.8rem;
  color: var(--st-text-muted);
  font-variant-numeric: tabular-nums;
}

.st-changelog-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  letter-spacing: -0.015em;
}

.st-changelog-summary {
  margin: 0.25rem 0 0.9rem;
  color: var(--st-text-muted);
}

.st-changelog-items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.55rem;
}

.st-changelog-item {
  display: flex;
  align-items: baseline;
  gap: 0.65rem;
  line-height: 1.45;
}

.st-changelog-tag {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 6.4rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.st-changelog-tag.is-novo {
  color: var(--st-success-strong);
  background: var(--st-success-soft);
}

.st-changelog-tag.is-melhoria {
  color: var(--st-brand);
  background: var(--st-brand-soft);
}

.st-changelog-tag.is-correcao {
  color: var(--st-warning-strong);
  background: var(--st-warning-soft);
}

@keyframes st-changelog-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
}

@media (max-width: 575.98px) {
  .st-changelog-item {
    flex-direction: column;
    gap: 0.2rem;
  }

  .st-changelog-tag {
    min-width: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .st-changelog-entry {
    animation: none;
  }
}
</style>
